import { showAlert, showConfirm } from './tgDialogs';
import React, { useCallback, useEffect, useState } from 'react';
import { apiUrl } from './api';
import { formatMicroUsdt } from './usdtFormat';
import {
  ORDER_CATEGORY_OPTIONS,
  orderCategoryLabel,
  buildOrderCategoryToken,
  subcategoryEntriesForMain
} from './categoryLabels';
import { PresenceStatusDot } from './avatarPresence';
import ServicePortfolioPanel from './ServicePortfolio';
import { payDigitalGoodWithStars } from './starsPay';
import { orderStatusLabel } from './orderStatusLabels';
import { CategoryEmblem, CategoryEmblemChip } from './CategoryEmblem';
import { EmptyState } from './EmptyState';
import './MarketplaceCards.css';
import './CategoryEmblem.css';

function displayNameUser(u: any) {
  return [u?.first_name, u?.last_name].filter(Boolean).join(' ') || 'Исполнитель';
}

function displayCustomerName(c: any) {
  return [c?.first_name, c?.last_name].filter(Boolean).join(' ') || 'Заказчик';
}

function formatDealDateTime(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Статус сделки по услуге для списка исполнителя. */
function serviceDealStatusLabel(orderStatus: string, responseStatus?: string | null) {
  if (orderStatus === 'open') {
    if (responseStatus === 'negotiating') return '💬 Обсуждение';
    if (responseStatus === 'pending_payment') return '💳 Ждёт оплаты';
    return orderStatusLabel('open');
  }
  if (orderStatus === 'in_progress') return '⚡ В работе';
  if (orderStatus === 'finished') return '🎉 Закрыто';
  if (orderStatus === 'cancelled') return '❌ Закрыто';
  return orderStatusLabel(orderStatus);
}

type ServiceOrderRow = {
  order_id: number;
  order_status: string;
  response_id?: number | null;
  response_status?: string | null;
  amount_usdt_micro: string;
  title?: string;
  created_at?: string;
  customer?: {
    id: number;
    first_name?: string | null;
    last_name?: string | null;
    username?: string | null;
    avatar_url?: string | null;
  };
  executor?: {
    id: number;
    first_name?: string | null;
    last_name?: string | null;
    username?: string | null;
    avatar_url?: string | null;
  } | null;
};

type ServiceRow = {
  id: number;
  title: string;
  description?: string | null;
  category: string;
  category_label?: string;
  price_usdt_micro: string | null;
  orders_count?: number;
  orders_limit?: number | null;
  orders_remaining?: number | null;
  has_portfolio?: boolean;
  portfolio_files_count?: number;
  portfolio_original_name?: string | null;
  is_active?: boolean;
  archived_at?: string | null;
  channel_published_at?: string | null;
  executor?: any;
};

const ORDERS_LIMIT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Безлимитно' },
  ...Array.from({ length: 25 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1)
  }))
];

const ORDERED_DEALS_PAGE_SIZE = 20;

function ordersLimitLabel(s: ServiceRow) {
  if (s.orders_limit == null) return `Заказано: ${s.orders_count ?? 0} · безлимит`;
  return `Заказано: ${s.orders_count ?? 0} из ${s.orders_limit}`;
}

/** Карточка услуги: компактный заголовок с ценой, описание, портфолио, CTA */
export function ServiceDetailModal({
  service,
  busy,
  onClose,
  onOpenProfile,
  onGetService,
  token,
  hideExecutor
}: {
  service: ServiceRow;
  busy?: boolean;
  onClose: () => void;
  onOpenProfile?: (userId: number) => void;
  onGetService: () => void;
  token?: string;
  hideExecutor?: boolean;
}) {
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const price =
    service.price_usdt_micro != null ? `${formatMicroUsdt(service.price_usdt_micro)} USDT` : '—';
  return (
    <div className="home-top-modal-overlay" role="dialog" aria-modal="true" aria-label="Услуга">
      <button type="button" className="home-top-modal-backdrop" aria-label="Закрыть" onClick={onClose} />
      <div
        className="home-top-modal"
        style={{ maxHeight: '85vh', overflow: 'auto', padding: '14px 14px 16px' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginBottom: 8
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 10
              }}
            >
              <h2
                className="home-top-modal__title"
                style={{ margin: 0, fontSize: 17, lineHeight: 1.25, flex: 1, minWidth: 0 }}
              >
                {service.title}
              </h2>
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 15,
                  fontWeight: 750,
                  color: 'var(--fc-usdt)',
                  whiteSpace: 'nowrap'
                }}
              >
                {price}
              </span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.35 }}>
              {service.category_label || orderCategoryLabel(service.category)}
            </p>
          </div>
          <button
            type="button"
            className="home-top-modal__close"
            onClick={onClose}
            style={{ marginTop: -2 }}
          >
            ✕
          </button>
        </div>

        {!hideExecutor && service.executor ? (
          <button
            type="button"
            onClick={() => onOpenProfile?.(Number(service.executor.id))}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(100,180,255,0.08)',
              border: '1px solid rgba(100,180,255,0.22)',
              borderRadius: 8,
              color: 'var(--fc-accent)',
              padding: '5px 9px',
              marginBottom: 10,
              fontSize: 12,
              cursor: 'pointer'
            }}
          >
            {displayNameUser(service.executor)}
          </button>
        ) : null}

        <div
          style={{
            marginBottom: 12,
            padding: '10px 11px',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)'
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              color: 'var(--fc-text-muted)',
              marginBottom: 6
            }}
          >
            Описание
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: 'var(--fc-text)',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap'
            }}
          >
            {service.description?.trim() || 'Описание не указано.'}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {token && service.has_portfolio ? (
            <button
              type="button"
              className="fc-btn fc-btn--ghost fc-btn--block fc-btn--sm"
              onClick={() => setPortfolioOpen(true)}
            >
              Портфолио
              {service.portfolio_files_count && service.portfolio_files_count > 0
                ? ` (${service.portfolio_files_count})`
                : ''}
            </button>
          ) : null}
          <button
            type="button"
            className="fc-btn fc-btn--primary fc-btn--block"
            disabled={busy}
            onClick={onGetService}
          >
            {busy ? '…' : 'Получить услугу'}
          </button>
          <p
            style={{
              margin: 0,
              fontSize: 11,
              color: 'var(--fc-text-muted)',
              lineHeight: 1.4,
              textAlign: 'center'
            }}
          >
            Откроется чат с исполнителем. Эскроу — когда исполнитель будет готов приступить.
          </p>
        </div>
      </div>
      {portfolioOpen && token ? (
        <ServicePortfolioPanel
          serviceId={service.id}
          serviceTitle={service.title}
          token={token}
          canUpload={false}
          onClose={() => setPortfolioOpen(false)}
        />
      ) : null}
    </div>
  );
}

export function ServicesCatalogScreen({
  token,
  mode,
  onBack,
  onOpenProfile,
  onOrdered,
  onOpenOrder,
  catalogPane: catalogPaneProp,
  onCatalogPaneChange,
  focusServiceId
}: {
  token: string;
  mode: 'catalog' | 'mine' | 'popular';
  onBack: () => void;
  onOpenProfile?: (userId: number) => void;
  onOrdered?: (orderId?: number) => void;
  onOpenOrder?: (orderId: number) => void;
  /** Controlled pane for customer catalog (browse | ordered) */
  catalogPane?: 'browse' | 'ordered';
  onCatalogPaneChange?: (pane: 'browse' | 'ordered') => void;
  /** Deep link: открыть карточку услуги по id */
  focusServiceId?: number | null;
}) {
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [detailService, setDetailService] = useState<ServiceRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [catSelect, setCatSelect] = useState('');
  const [subSelect, setSubSelect] = useState('');
  const [customCat, setCustomCat] = useState('');
  const [price, setPrice] = useState('');
  const [ordersLimit, setOrdersLimit] = useState('');
  const [portfolioFiles, setPortfolioFiles] = useState<File[]>([]);
  const [portfolioView, setPortfolioView] = useState<{ id: number; title: string; canUpload: boolean } | null>(
    null
  );
  const [expandedServiceId, setExpandedServiceId] = useState<number | null>(null);
  const [serviceOrdersTab, setServiceOrdersTab] = useState<'active' | 'archive'>('active');
  const [serviceOrders, setServiceOrders] = useState<ServiceOrderRow[]>([]);
  const [serviceOrdersLoading, setServiceOrdersLoading] = useState(false);
  const [mineArchived, setMineArchived] = useState(false);
  /** Заказчик: каталог услуг vs свои заказы по услугам */
  const [catalogPaneLocal, setCatalogPaneLocal] = useState<'browse' | 'ordered'>('browse');
  const catalogPane = catalogPaneProp ?? catalogPaneLocal;
  const setCatalogPane = (pane: 'browse' | 'ordered' | ((prev: 'browse' | 'ordered') => 'browse' | 'ordered')) => {
    const next = typeof pane === 'function' ? pane(catalogPane) : pane;
    if (catalogPaneProp == null) setCatalogPaneLocal(next);
    onCatalogPaneChange?.(next);
  };
  const [orderedArchived, setOrderedArchived] = useState(false);
  const [orderedDeals, setOrderedDeals] = useState<ServiceOrderRow[]>([]);
  const [orderedLoading, setOrderedLoading] = useState(false);
  const [orderedPage, setOrderedPage] = useState(0);
  const [orderedTotal, setOrderedTotal] = useState(0);
  const [repostId, setRepostId] = useState<number | null>(null);
  const [repostLimit, setRepostLimit] = useState('');
  const [maxServices, setMaxServices] = useState(10);
  const [freePlacementsLeft, setFreePlacementsLeft] = useState(10);
  const [freePlacementsUsed, setFreePlacementsUsed] = useState(0);
  const [freePlacementsDefault, setFreePlacementsDefault] = useState(10);
  const [paidPlacementsLeft, setPaidPlacementsLeft] = useState(0);
  const [servicePostingPacks, setServicePostingPacks] = useState<
    Array<{ sku: string; slots: number; stars: number }>
  >([
    { sku: 'service_posting_1', slots: 1, stars: 50 },
    { sku: 'service_posting_5', slots: 5, stars: 200 },
    { sku: 'service_posting_10', slots: 10, stars: 400 }
  ]);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyBusy, setBuyBusy] = useState(false);
  const [executorVipActive, setExecutorVipActive] = useState(false);
  const [serviceChannelVipLeft, setServiceChannelVipLeft] = useState(0);
  const [serviceChannelVipQuota, setServiceChannelVipQuota] = useState(10);
  const [serviceChannelCredits, setServiceChannelCredits] = useState(0);
  const [serviceChannelPacks, setServiceChannelPacks] = useState<
    Array<{ sku: string; slots: number; stars: number }>
  >([
    { sku: 'service_channel_1', slots: 1, stars: 75 },
    { sku: 'service_channel_5', slots: 5, stars: 350 },
    { sku: 'service_channel_10', slots: 10, stars: 650 }
  ]);
  const [buyChannelOpen, setBuyChannelOpen] = useState(false);
  const [channelBusyId, setChannelBusyId] = useState<number | null>(null);

  const applyQuota = (d: any) => {
    if (d.max != null) setMaxServices(Number(d.max) || 10);
    if (d.free_executor_service_placements_left != null) {
      setFreePlacementsLeft(Number(d.free_executor_service_placements_left) || 0);
    }
    if (d.free_executor_service_placements_used != null) {
      setFreePlacementsUsed(Number(d.free_executor_service_placements_used) || 0);
    }
    if (d.free_executor_service_placements_default != null) {
      setFreePlacementsDefault(Number(d.free_executor_service_placements_default) || 10);
    }
    if (d.paid_executor_service_placements_left != null) {
      setPaidPlacementsLeft(Number(d.paid_executor_service_placements_left) || 0);
    }
    if (Array.isArray(d.service_posting_packs) && d.service_posting_packs.length) {
      setServicePostingPacks(
        d.service_posting_packs.map((p: any) => ({
          sku: String(p.sku),
          slots: Number(p.slots) || 0,
          stars: Number(p.stars) || 0
        }))
      );
    }
    if (d.executor_vip_active != null) setExecutorVipActive(Boolean(d.executor_vip_active));
    if (d.executor_service_channel_posts_remaining != null) {
      setServiceChannelVipLeft(Number(d.executor_service_channel_posts_remaining) || 0);
    }
    if (d.executor_vip_service_channel_posts_per_period != null) {
      setServiceChannelVipQuota(Number(d.executor_vip_service_channel_posts_per_period) || 10);
    }
    if (d.executor_service_channel_post_credits != null) {
      setServiceChannelCredits(Number(d.executor_service_channel_post_credits) || 0);
    }
    if (Array.isArray(d.service_channel_packs) && d.service_channel_packs.length) {
      setServiceChannelPacks(
        d.service_channel_packs.map((p: any) => ({
          sku: String(p.sku),
          slots: Number(p.slots) || 0,
          stars: Number(p.stars) || 0
        }))
      );
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'mine') {
        const qs = mineArchived ? '?archived=1' : '';
        const r = await fetch(apiUrl(`/api/executor-services/mine${qs}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Ошибка');
        setServices(d.services || []);
        applyQuota(d);
      } else {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        if (subcategory) params.set('subcategory', subcategory);
        if (searchQ.trim()) params.set('q', searchQ.trim());
        if (mode === 'popular') params.set('popular', '1');
        params.set('limit', mode === 'popular' ? '25' : '40');
        const r = await fetch(apiUrl(`/api/executor-services?${params}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Ошибка');
        setServices(d.services || []);
      }
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось загрузить услуги');
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [token, mode, category, subcategory, searchQ, mineArchived]);

  const loadOrderedDeals = useCallback(async () => {
    if (mode !== 'catalog' && mode !== 'popular') return;
    setOrderedLoading(true);
    try {
      const params = new URLSearchParams();
      if (orderedArchived) params.set('archived', '1');
      params.set('limit', String(ORDERED_DEALS_PAGE_SIZE));
      params.set('offset', String(orderedPage * ORDERED_DEALS_PAGE_SIZE));
      const r = await fetch(apiUrl(`/api/executor-services/ordered?${params}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ошибка');
      setOrderedDeals(Array.isArray(d.orders) ? d.orders : []);
      setOrderedTotal(Number(d.total) || 0);
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось загрузить заказы услуг');
      setOrderedDeals([]);
      setOrderedTotal(0);
    } finally {
      setOrderedLoading(false);
    }
  }, [token, mode, orderedArchived, orderedPage]);

  useEffect(() => {
    if (mode === 'catalog' && catalogPane === 'ordered') {
      void loadOrderedDeals();
      return;
    }
    void load();
  }, [load, loadOrderedDeals, mode, catalogPane]);

  useEffect(() => {
    setOrderedPage(0);
  }, [orderedArchived]);

  useEffect(() => {
    if (catalogPaneProp != null) setCatalogPaneLocal(catalogPaneProp);
  }, [catalogPaneProp]);

  useEffect(() => {
    const sid = focusServiceId != null ? Number(focusServiceId) : NaN;
    if (!Number.isFinite(sid) || sid < 1) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(apiUrl(`/api/executor-services/${sid}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json().catch(() => ({}));
        if (cancelled || !r.ok || !d.service) return;
        setDetailService(d.service);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [focusServiceId, token]);

  const loadServiceOrders = useCallback(
    async (serviceId: number, tab: 'active' | 'archive') => {
      setServiceOrdersLoading(true);
      try {
        const qs = tab === 'archive' ? '?archived=1' : '';
        const r = await fetch(apiUrl(`/api/executor-services/mine/${serviceId}/orders${qs}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Ошибка');
        setServiceOrders(Array.isArray(d.orders) ? d.orders : []);
      } catch (e: any) {
        showAlert(e?.message || 'Не удалось загрузить заказы');
        setServiceOrders([]);
      } finally {
        setServiceOrdersLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (mode !== 'mine' || expandedServiceId == null) return;
    void loadServiceOrders(expandedServiceId, serviceOrdersTab);
  }, [mode, expandedServiceId, serviceOrdersTab, loadServiceOrders]);

  useEffect(() => {
    setExpandedServiceId(null);
    setServiceOrders([]);
    setServiceOrdersTab('active');
  }, [mineArchived]);

  const saveMine = async () => {
    const effectiveCategory =
      catSelect === 'custom' || catSelect === 'other'
        ? customCat.trim()
          ? `customMain:${customCat.trim()}`
          : ''
        : buildOrderCategoryToken(catSelect.trim(), subSelect.trim() || undefined);
    if (!title.trim() || !effectiveCategory || !price.trim()) {
      showAlert('Заполните название, категорию и цену');
      return;
    }
    if (catSelect && catSelect !== 'custom' && catSelect !== 'other') {
      const subs = subcategoryEntriesForMain(catSelect).filter((s) => s.key !== 'custom');
      if (subs.length > 0 && !subSelect.trim()) {
        showAlert('Выберите подкатегорию');
        return;
      }
    }
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      if (desc.trim()) fd.append('description', desc.trim());
      fd.append('category', effectiveCategory);
      fd.append('price', price.trim());
      if (ordersLimit) fd.append('orders_limit', ordersLimit);
      else fd.append('orders_limit', '');
      for (const f of portfolioFiles.slice(0, 10)) {
        fd.append('portfolio', f);
      }

      const r = await fetch(apiUrl('/api/executor-services/mine'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ошибка');
      setFormOpen(false);
      setTitle('');
      setDesc('');
      setCatSelect('');
      setSubSelect('');
      setCustomCat('');
      setPrice('');
      setOrdersLimit('');
      setPortfolioFiles([]);
      setMineArchived(false);
      applyQuota(d);
      await load();
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось сохранить');
    }
  };

  const removeMine = async (id: number) => {
    if (!await showConfirm('Отправить услугу в архив?')) return;
    try {
      const r = await fetch(apiUrl(`/api/executor-services/mine/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Ошибка');
      await load();
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось архивировать');
    }
  };

  const repostMine = async () => {
    if (repostId == null) return;
    try {
      const r = await fetch(apiUrl(`/api/executor-services/mine/${repostId}/repost`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orders_limit: repostLimit || null })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ошибка');
      setRepostId(null);
      setRepostLimit('');
      setMineArchived(false);
      applyQuota(d);
      await load();
      showAlert('Услуга снова на витрине.');
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось разместить');
    }
  };

  const buyServicePlacement = async (sku: string) => {
    setBuyBusy(true);
    try {
      const status = await payDigitalGoodWithStars(token, sku);
      if (status === 'paid') {
        showAlert('Оплата прошла — слоты зачислены.');
        setBuyOpen(false);
        await load();
      } else if (status === 'cancelled') {
        /* noop */
      } else {
        showAlert('Оплата не завершена');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось оплатить');
    } finally {
      setBuyBusy(false);
    }
  };

  const buyServiceChannelPack = async (sku: string) => {
    setBuyBusy(true);
    try {
      const status = await payDigitalGoodWithStars(token, sku);
      if (status === 'paid') {
        showAlert('Оплата прошла — бусты услуг в канал зачислены.');
        setBuyChannelOpen(false);
        await load();
      } else if (status === 'cancelled') {
        /* noop */
      } else {
        showAlert('Оплата не завершена');
      }
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось оплатить');
    } finally {
      setBuyBusy(false);
    }
  };

  const publishServiceChannel = async (id: number) => {
    setChannelBusyId(id);
    try {
      const r = await fetch(apiUrl(`/api/executor-services/mine/${id}/publish-channel`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (d.code === 'no_service_channel_posts' || d.code === 'executor_vip_service_channel_quota_exhausted') {
          setBuyChannelOpen(true);
        }
        throw new Error(d.error || 'Не удалось опубликовать');
      }
      if (d.executor_service_channel_posts_remaining != null) {
        setServiceChannelVipLeft(Number(d.executor_service_channel_posts_remaining) || 0);
      }
      if (d.executor_service_channel_post_credits != null) {
        setServiceChannelCredits(Number(d.executor_service_channel_post_credits) || 0);
      }
      applyQuota(d);
      await load();
      showAlert('Услуга опубликована в канале.');
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось опубликовать');
    } finally {
      setChannelBusyId(null);
    }
  };

  const placementsAvailable = freePlacementsLeft + paidPlacementsLeft;
  const freeExhausted = freePlacementsLeft <= 0;
  const canAddService = !mineArchived && services.length < maxServices && placementsAvailable > 0;
  const serviceChannelAvailable =
    serviceChannelCredits > 0 || (executorVipActive && serviceChannelVipLeft > 0);

  const orderService = async (id: number) => {
    setBusyId(id);
    try {
      const r = await fetch(apiUrl(`/api/executor-services/${id}/order`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ошибка');
      setDetailService(null);
      const oid = Number(d.order?.id);
      onOrdered?.(Number.isFinite(oid) ? oid : undefined);
      await load();
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось заказать');
    } finally {
      setBusyId(null);
    }
  };

  const titleMap = {
    catalog: '🛠 Услуги',
    mine: '🛠 Мои услуги',
    popular: '🔥 Популярные услуги'
  } as const;

  return (
    <div className="App">
      <header className="App-header" style={{ justifyContent: 'flex-start', paddingTop: 12 }}>
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            margin: '0 auto 10px',
            padding:
              mode === 'catalog' && catalogPane === 'ordered' ? '0 2px 0 8px' : '0 8px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (mode === 'catalog' && catalogPane === 'ordered') {
                setCatalogPane('browse');
                return;
              }
              onBack();
            }}
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: 'var(--fc-card-hover)',
              color: 'var(--fc-text)',
              border: '1px solid var(--fc-border)',
              cursor: 'pointer',
              fontSize: 20
            }}
          >
            ←
          </button>
          <h2 style={{ margin: 0, flex: 1 }}>
            {mode === 'mine'
              ? mineArchived
                ? '📦 Архив услуг'
                : '🛠 Мои услуги'
              : mode === 'catalog' && catalogPane === 'ordered'
                ? orderedArchived
                  ? '📦 Архив заказов'
                  : '📋 Заказано'
                : titleMap[mode]}
          </h2>
          {mode === 'mine' ? (
            <button
              type="button"
              className="fc-btn fc-btn--ghost"
              style={{ fontSize: 13, padding: '8px 10px' }}
              onClick={() => {
                setFormOpen(false);
                setMineArchived((v) => !v);
              }}
            >
              {mineArchived ? 'Активные' : 'Архив'}
            </button>
          ) : mode === 'catalog' && catalogPane === 'ordered' ? (
            <button
              type="button"
              className="fc-btn fc-btn--ghost"
              style={{
                fontSize: 14,
                fontWeight: 600,
                padding: '0 12px',
                height: 36,
                borderRadius: 10,
                boxSizing: 'border-box',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginRight: 0
              }}
              onClick={() => setOrderedArchived((v) => !v)}
            >
              {orderedArchived ? 'Активные' : 'Архив'}
            </button>
          ) : (
            <button
              type="button"
              aria-label="Поиск"
              onClick={() => {
                setSearchDraft(searchQ);
                setSearchOpen(true);
              }}
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: searchQ ? 'rgba(var(--fc-accent-rgb),0.25)' : 'var(--fc-card-hover)',
                color: 'var(--fc-text)',
                border: '1px solid var(--fc-border)',
                cursor: 'pointer',
                fontSize: 18
              }}
            >
              🔍
            </button>
          )}
        </div>

        {mode !== 'mine' && !(mode === 'catalog' && catalogPane === 'ordered') ? (
          <div style={{ maxWidth: 420, margin: '0 auto 10px', padding: '0 8px', width: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setSubcategory('');
                }}
                className="fc-select"
                style={{
                  flex: 1,
                  minWidth: 0,
                  margin: 0,
                  height: 36,
                  fontSize: 13,
                  padding: '6px 10px'
                }}
              >
                <option value="">Все категории</option>
                {ORDER_CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {mode === 'catalog' ? (
                <button
                  type="button"
                  className="fc-btn fc-btn--sm fc-btn--primary"
                  style={{ flexShrink: 0, fontSize: 12, padding: '8px 10px', whiteSpace: 'nowrap', height: 36 }}
                  onClick={() => {
                    setSearchOpen(false);
                    setCatalogPane('ordered');
                  }}
                >
                  Заказано
                </button>
              ) : null}
            </div>
            {category && subcategoryEntriesForMain(category).filter((s) => s.key !== 'custom').length > 0 ? (
              <select
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                className="fc-select"
                style={{
                  width: '100%',
                  margin: '8px 0 0',
                  height: 36,
                  fontSize: 13,
                  padding: '6px 10px'
                }}
              >
                <option value="">Все подкатегории</option>
                {subcategoryEntriesForMain(category)
                  .filter((s) => s.key !== 'custom')
                  .map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
              </select>
            ) : null}
            {searchQ ? (
              <div
                style={{
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: 'var(--fc-text-muted)'
                }}
              >
                <span>
                  Поиск: <b style={{ color: 'var(--fc-text)' }}>{searchQ}</b>
                </span>
                <button
                  type="button"
                  className="fc-btn fc-btn--ghost"
                  style={{ fontSize: 12, padding: '4px 8px' }}
                  onClick={() => {
                    setSearchQ('');
                    setSearchDraft('');
                  }}
                >
                  Сбросить
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {mode === 'mine' ? (
          <div style={{ maxWidth: 420, margin: '0 auto 8px', padding: '0 8px', width: '100%', boxSizing: 'border-box' }}>
            {!mineArchived ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginBottom: formOpen || freeExhausted ? 8 : 0
                }}
              >
                <p style={{ fontSize: 12, color: 'var(--fc-text-muted)', margin: 0, lineHeight: 1.35, flex: 1, minWidth: 0 }}>
                  Бесплатных:{' '}
                  <strong style={{ color: 'var(--fc-text)' }}>
                    {freePlacementsUsed} из {freePlacementsDefault}
                  </strong>
                  {paidPlacementsLeft > 0 ? (
                    <>
                      {' '}
                      · слотов: <strong style={{ color: 'var(--fc-text)' }}>{paidPlacementsLeft}</strong>
                    </>
                  ) : null}
                </p>
                {!formOpen ? (
                  <button
                    type="button"
                    className="fc-btn fc-btn--sm"
                    style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    onClick={() => {
                      if (!canAddService) {
                        if (placementsAvailable <= 0) {
                          setBuyOpen(true);
                          return;
                        }
                        showAlert(`Достигнут лимит активных услуг (${maxServices})`);
                        return;
                      }
                      setFormOpen(true);
                    }}
                    disabled={services.length >= maxServices && placementsAvailable > 0}
                  >
                    ➕ Добавить
                  </button>
                ) : null}
              </div>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--fc-text-muted)', margin: '0 0 8px' }}>
                Архив: услуги с исчерпанным лимитом или снятые с витрины. Можно разместить заново.
              </p>
            )}
            {!mineArchived && freeExhausted ? (
              <button
                type="button"
                className="fc-btn fc-btn--sm"
                style={{ width: '100%', marginBottom: 8 }}
                onClick={() => setBuyOpen(true)}
              >
                ⭐ Купить размещение
              </button>
            ) : null}
            {!mineArchived ? (
              <div
                style={{
                  marginBottom: 8,
                  padding: '8px 10px',
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.03)'
                }}
              >
                <p style={{ margin: 0, fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>
                  Канал биржи:{' '}
                  {executorVipActive ? (
                    <>
                      VIP <strong style={{ color: 'var(--fc-text)' }}>{serviceChannelVipLeft}</strong> из{' '}
                      {serviceChannelVipQuota}
                    </>
                  ) : (
                    <span>нужен VIP для квоты {serviceChannelVipQuota}</span>
                  )}
                  {serviceChannelCredits > 0 ? (
                    <>
                      {' '}
                      · купленные: <strong style={{ color: 'var(--fc-text)' }}>{serviceChannelCredits}</strong>
                    </>
                  ) : null}
                </p>
                <button
                  type="button"
                  className="fc-btn fc-btn--sm fc-btn--ghost"
                  style={{ width: '100%', marginTop: 6 }}
                  onClick={() => setBuyChannelOpen(true)}
                >
                  ⭐ Купить посты в канал
                </button>
              </div>
            ) : null}
            {!mineArchived && formOpen ? (
              <div className="fc-form fc-form--spaced">
                <input className="fc-input fc-input--full" placeholder="Название" value={title} onChange={(e) => setTitle(e.target.value)} />
                <textarea className="fc-input fc-input--full" placeholder="Описание" value={desc} onChange={(e) => setDesc(e.target.value)} style={{ minHeight: 70 }} />
                <div className="fc-cat-chip-row" role="listbox" aria-label="Категория услуги">
                  {ORDER_CATEGORY_OPTIONS.map((o) => (
                    <CategoryEmblemChip
                      key={o.value}
                      value={o.value}
                      label={o.label}
                      selected={catSelect === o.value}
                      onSelect={(v) => {
                        setCatSelect(v);
                        setSubSelect('');
                        setCustomCat('');
                      }}
                    />
                  ))}
                  <CategoryEmblemChip
                    value="custom"
                    label="Прочее"
                    selected={catSelect === 'custom' || catSelect === 'other'}
                    onSelect={() => {
                      setCatSelect('custom');
                      setSubSelect('');
                    }}
                  />
                </div>
                {catSelect && catSelect !== 'custom' && subcategoryEntriesForMain(catSelect).length > 0 ? (
                  <select className="fc-input fc-input--full" value={subSelect} onChange={(e) => setSubSelect(e.target.value)}>
                    <option value="">Подкатегория</option>
                    {subcategoryEntriesForMain(catSelect).map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                ) : null}
                {(catSelect === 'custom' || catSelect === 'other') && (
                  <input
                    className="fc-input fc-input--full"
                    placeholder="Своя категория"
                    value={customCat}
                    onChange={(e) => setCustomCat(e.target.value)}
                  />
                )}
                <input className="fc-input fc-input--full" placeholder="Цена USDT" value={price} onChange={(e) => setPrice(e.target.value)} />
                <select className="fc-input fc-input--full" value={ordersLimit} onChange={(e) => setOrdersLimit(e.target.value)}>
                  {ORDERS_LIMIT_OPTIONS.map((o) => (
                    <option key={o.value || 'unlim'} value={o.value}>
                      Лимит заказов: {o.label}
                    </option>
                  ))}
                </select>
                <label style={{ fontSize: 13, color: 'var(--fc-text-muted)', textAlign: 'left' }}>
                  Портфолио (до 10 файлов)
                  <input
                    type="file"
                    multiple
                    style={{ display: 'block', marginTop: 6, width: '100%' }}
                    onChange={(e) => setPortfolioFiles(Array.from(e.target.files || []).slice(0, 10))}
                  />
                  {portfolioFiles.length > 0 ? (
                    <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: 'var(--fc-text-muted)' }}>
                      Выбрано: {portfolioFiles.length} · {portfolioFiles.map((f) => f.name).join(', ')}
                    </span>
                  ) : null}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="fc-btn" onClick={() => void saveMine()}>
                    Сохранить
                  </button>
                  <button type="button" className="fc-btn fc-btn--ghost" onClick={() => setFormOpen(false)}>
                    Отмена
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {mode === 'catalog' && catalogPane === 'ordered' ? (
          orderedLoading ? (
            <p>Загрузка…</p>
          ) : orderedDeals.length === 0 ? (
            <EmptyState
              icon={orderedArchived ? '📦' : '🛍️'}
              title={orderedArchived ? 'В архиве пока нет заказов услуг' : 'Пока нет активных заказов услуг'}
              subtitle={
                orderedArchived
                  ? undefined
                  : 'Оформите услугу в каталоге — она появится здесь, и вы сможете снова открыть чат.'
              }
            />
          ) : (
            <div
              style={{
                width: '100%',
                maxWidth: 420,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '0 8px 20px'
              }}
            >
              {orderedDeals.map((o) => (
                <button
                  key={o.order_id}
                  type="button"
                  onClick={() => onOpenOrder?.(o.order_id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: '1px solid var(--fc-border)',
                    background: 'var(--fc-card)',
                    color: 'var(--fc-text)',
                    cursor: onOpenOrder ? 'pointer' : 'default'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      alignItems: 'baseline'
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.25, flex: 1, minWidth: 0 }}>
                      {o.title || 'Услуга'}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fc-usdt)', whiteSpace: 'nowrap' }}>
                      {formatMicroUsdt(o.amount_usdt_micro)} USDT
                    </span>
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: 'var(--fc-text-muted)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      alignItems: 'flex-start'
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div>{displayNameUser(o.executor)}</div>
                      <div style={{ marginTop: 3, fontSize: 11, color: 'var(--fc-text-muted)' }}>
                        {serviceDealStatusLabel(o.order_status, o.response_status)}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ color: 'var(--fc-accent)', fontSize: 12 }}>Чат →</div>
                      {o.created_at ? (
                        <div style={{ marginTop: 3, fontSize: 11, color: 'var(--fc-text-muted)', whiteSpace: 'nowrap' }}>
                          {formatDealDateTime(o.created_at)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </button>
              ))}
              {orderedTotal > ORDERED_DEALS_PAGE_SIZE ? (
                <div
                  style={{
                    marginTop: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8
                  }}
                >
                  <button
                    type="button"
                    className="fc-btn fc-btn--ghost fc-btn--sm"
                    disabled={orderedPage <= 0 || orderedLoading}
                    onClick={() => setOrderedPage((p) => Math.max(0, p - 1))}
                  >
                    ←
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--fc-text-muted)' }}>
                    {orderedPage + 1} / {Math.max(1, Math.ceil(orderedTotal / ORDERED_DEALS_PAGE_SIZE))}
                  </span>
                  <button
                    type="button"
                    className="fc-btn fc-btn--ghost fc-btn--sm"
                    disabled={
                      orderedPage + 1 >= Math.ceil(orderedTotal / ORDERED_DEALS_PAGE_SIZE) || orderedLoading
                    }
                    onClick={() => setOrderedPage((p) => p + 1)}
                  >
                    →
                  </button>
                </div>
              ) : null}
            </div>
          )
        ) : loading ? (
          <p>Загрузка…</p>
        ) : services.length === 0 ? (
          <p style={{ opacity: 0.8 }}>
            {mode === 'mine'
              ? mineArchived
                ? 'Архив пуст.'
                : 'Пока нет услуг — добавьте первую.'
              : 'Пока нет услуг в этой категории.'}
          </p>
        ) : (
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: '0 8px 20px'
            }}
          >
            {services.map((s) => (
              <div
                key={s.id}
                className="mk-service-card"
                style={{
                  flexDirection: 'row',
                  alignItems: 'stretch',
                  border:
                    mode === 'mine' && expandedServiceId === s.id
                      ? '1px solid rgba(var(--fc-accent-rgb), 0.45)'
                      : undefined
                }}
              >
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 8,
                    width: '100%'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (mode === 'mine') {
                        setExpandedServiceId((prev) => {
                          if (prev === s.id) return null;
                          setServiceOrdersTab('active');
                          return s.id;
                        });
                        return;
                      }
                      setDetailService(s);
                    }}
                    style={{
                      display: 'block',
                      flex: 1,
                      minWidth: 0,
                      background: 'none',
                      border: 0,
                      padding: 0,
                      margin: 0,
                      textAlign: 'left',
                      color: 'inherit',
                      cursor: 'pointer'
                    }}
                  >
                    <div className="mk-service-card__title">{s.title}</div>
                    <div className="mk-service-card__price">
                      {s.price_usdt_micro != null
                        ? `От ${formatMicroUsdt(s.price_usdt_micro)} USDT`
                        : '—'}
                    </div>
                    <div className="mk-service-card__meta">
                      {s.category_label || orderCategoryLabel(s.category)}
                      {s.executor
                        ? ` · ${[s.executor.first_name, s.executor.last_name].filter(Boolean).join(' ').trim() || 'Исполнитель'}`
                        : ''}
                    </div>
                    {s.description ? (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          color: 'var(--fc-text-muted)',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical' as const,
                          overflow: 'hidden'
                        }}
                      >
                        {s.description}
                      </div>
                    ) : null}
                  </button>
                  {mode === 'mine' ? (
                    <button
                      type="button"
                      className="fc-btn fc-btn--sm"
                      style={{
                        flexShrink: 0,
                        padding: '5px 10px',
                        fontSize: 12,
                        fontWeight: 650,
                        background:
                          expandedServiceId === s.id
                            ? 'rgba(var(--fc-accent-rgb), 0.28)'
                            : 'rgba(var(--fc-accent-rgb), 0.16)',
                        border: '1px solid rgba(100, 180, 255, 0.45)',
                        color: 'var(--fc-text)',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => {
                        setExpandedServiceId((prev) => {
                          if (prev === s.id) return null;
                          setServiceOrdersTab('active');
                          return s.id;
                        });
                      }}
                    >
                      {expandedServiceId === s.id ? 'Свернуть ▴' : 'Заказчики ▾'}
                    </button>
                  ) : s.executor ? (
                    <button
                      type="button"
                      onClick={() => onOpenProfile?.(Number(s.executor.id))}
                      title={displayNameUser(s.executor)}
                      style={{
                        flexShrink: 0,
                        maxWidth: 118,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 10,
                        padding: '5px 6px',
                        color: 'var(--fc-text)',
                        cursor: 'pointer'
                      }}
                    >
                      <span style={{ position: 'relative', width: 28, height: 28 }}>
                        {s.executor.avatar_url ? (
                          <img
                            src={s.executor.avatar_url}
                            alt=""
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              background: 'var(--fc-accent)',
                              color: 'var(--fc-on-accent)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11
                            }}
                          >
                            {s.executor.first_name?.[0]}
                          </span>
                        )}
                        <PresenceStatusDot lastSeenAt={s.executor.last_seen_at} />
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          lineHeight: 1.2,
                          textAlign: 'center',
                          maxWidth: 100,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {displayNameUser(s.executor)}
                      </span>
                    </button>
                  ) : null}
                </div>
                {mode === 'mine' && expandedServiceId === s.id ? (
                  <div
                    style={{
                      marginTop: 8,
                      paddingTop: 8,
                      borderTop: '1px solid rgba(255,255,255,0.08)'
                    }}
                  >
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      <button
                        type="button"
                        className={`fc-btn fc-btn--sm${
                          serviceOrdersTab === 'active' ? ' fc-btn--primary' : ' fc-btn--ghost'
                        }`}
                        style={{ flex: 1 }}
                        onClick={() => setServiceOrdersTab('active')}
                      >
                        Активные
                      </button>
                      <button
                        type="button"
                        className={`fc-btn fc-btn--sm${
                          serviceOrdersTab === 'archive' ? ' fc-btn--primary' : ' fc-btn--ghost'
                        }`}
                        style={{ flex: 1 }}
                        onClick={() => setServiceOrdersTab('archive')}
                      >
                        Архив сделок
                      </button>
                    </div>
                    {serviceOrdersLoading ? (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--fc-text-muted)' }}>Загрузка…</p>
                    ) : serviceOrders.length === 0 ? (
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>
                        {serviceOrdersTab === 'archive'
                          ? 'Нет завершённых заказов по этой услуге.'
                          : 'Пока нет активных заказов. Когда заказчик оформит услугу — он появится здесь.'}
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {serviceOrders.map((o) => (
                          <button
                            key={o.order_id}
                            type="button"
                            onClick={() => onOpenOrder?.(o.order_id)}
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              padding: '8px 10px',
                              borderRadius: 9,
                              border: '1px solid var(--fc-border)',
                              background: 'var(--fc-card)',
                              color: 'var(--fc-text)',
                              cursor: onOpenOrder ? 'pointer' : 'default'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 8,
                                alignItems: 'baseline'
                              }}
                            >
                              <span style={{ fontWeight: 650, fontSize: 13 }}>
                                {displayCustomerName(o.customer)}
                              </span>
                              <span style={{ fontSize: 12, color: 'var(--fc-usdt)', whiteSpace: 'nowrap' }}>
                                {formatMicroUsdt(o.amount_usdt_micro)} USDT
                              </span>
                            </div>
                            <div
                              style={{
                                marginTop: 3,
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 8,
                                fontSize: 11,
                                color: 'var(--fc-text-muted)'
                              }}
                            >
                              <span>
                                {serviceDealStatusLabel(o.order_status, o.response_status)}
                              </span>
                              <span style={{ color: 'var(--fc-accent)' }}>Чат →</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6
                  }}
                >
                  {s.has_portfolio || mode === 'mine' ? (
                    <button
                      type="button"
                      className="fc-btn fc-btn--ghost fc-btn--sm"
                      onClick={() =>
                        setPortfolioView({
                          id: s.id,
                          title: s.title,
                          canUpload: mode === 'mine' && !mineArchived
                        })
                      }
                    >
                      Портфолио
                      {s.portfolio_files_count && s.portfolio_files_count > 0
                        ? ` (${s.portfolio_files_count})`
                        : ''}
                    </button>
                  ) : null}
                  {mode === 'mine' && !mineArchived ? (
                    <button
                      type="button"
                      className="fc-btn fc-btn--ghost fc-btn--sm"
                      onClick={() => void removeMine(s.id)}
                    >
                      В архив
                    </button>
                  ) : null}
                  {mode === 'mine' && !mineArchived ? (
                    s.channel_published_at ? (
                      <span
                        style={{
                          fontSize: 12,
                          color: 'var(--fc-text-muted)',
                          alignSelf: 'center',
                          padding: '0 4px'
                        }}
                      >
                        ✓ В канале
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="fc-btn fc-btn--sm"
                        disabled={channelBusyId === s.id}
                        onClick={() => {
                          if (!serviceChannelAvailable) {
                            setBuyChannelOpen(true);
                            return;
                          }
                          void publishServiceChannel(s.id);
                        }}
                      >
                        {channelBusyId === s.id ? '…' : 'В канал'}
                      </button>
                    )
                  ) : null}
                  {mode === 'mine' && mineArchived ? (
                    <button
                      type="button"
                      className="fc-btn fc-btn--sm"
                      onClick={() => {
                        if (placementsAvailable <= 0) {
                          setBuyOpen(true);
                          return;
                        }
                        setRepostId(s.id);
                        setRepostLimit(s.orders_limit != null ? String(s.orders_limit) : '');
                      }}
                    >
                      Разместить заново
                    </button>
                  ) : null}
                  {mode !== 'mine' ? (
                    <button
                      type="button"
                      className="fc-btn fc-btn--sm"
                      onClick={() => setDetailService(s)}
                    >
                      Смотреть
                    </button>
                  ) : null}
                </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </header>

      {repostId != null ? (
        <div className="home-top-modal-overlay" role="dialog" aria-modal="true" aria-label="Разместить услугу">
          <button type="button" className="home-top-modal-backdrop" aria-label="Закрыть" onClick={() => setRepostId(null)} />
          <div className="home-top-modal">
            <div className="home-top-modal__head">
              <h2 className="home-top-modal__title">Разместить заново</h2>
              <button type="button" className="home-top-modal__close" onClick={() => setRepostId(null)}>
                ✕
              </button>
            </div>
            <p className="home-top-modal__hint">Счётчик заказов обнулится. Выберите новый лимит.</p>
            {(() => {
              const s = services.find((x) => x.id === repostId);
              return s ? (
                <p style={{ fontSize: 13, color: 'var(--fc-text-muted)', marginTop: 0 }}>
                  Было: <b style={{ color: 'var(--fc-text)' }}>{ordersLimitLabel(s)}</b>
                </p>
              ) : null;
            })()}
            <select
              className="fc-input fc-input--full"
              value={repostLimit}
              onChange={(e) => setRepostLimit(e.target.value)}
              style={{ marginBottom: 10 }}
            >
              {ORDERS_LIMIT_OPTIONS.map((o) => (
                <option key={o.value || 'unlim'} value={o.value}>
                  Лимит заказов: {o.label}
                </option>
              ))}
            </select>
            <button type="button" className="fc-btn" style={{ width: '100%' }} onClick={() => void repostMine()}>
              Разместить
            </button>
          </div>
        </div>
      ) : null}

      {searchOpen ? (
        <div className="home-top-modal-overlay" role="dialog" aria-modal="true" aria-label="Поиск услуг">
          <button
            type="button"
            className="home-top-modal-backdrop"
            aria-label="Закрыть"
            onClick={() => setSearchOpen(false)}
          />
          <div className="home-top-modal">
            <div className="home-top-modal__head">
              <h2 className="home-top-modal__title">Поиск услуг</h2>
              <button type="button" className="home-top-modal__close" onClick={() => setSearchOpen(false)}>
                ✕
              </button>
            </div>
            <p className="home-top-modal__hint">
              Ищет по названию, описанию, категории и подкатегории (например «сайт»).
            </p>
            <input
              className="fc-input fc-input--full"
              autoFocus
              placeholder="Ключевое слово…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchQ(searchDraft.trim());
                  setSearchOpen(false);
                }
              }}
              style={{ marginBottom: 10 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="fc-btn"
                style={{ flex: 1 }}
                onClick={() => {
                  setSearchQ(searchDraft.trim());
                  setSearchOpen(false);
                }}
              >
                Найти
              </button>
              <button
                type="button"
                className="fc-btn fc-btn--ghost"
                style={{ flex: 1 }}
                onClick={() => {
                  setSearchDraft('');
                  setSearchQ('');
                  setSearchOpen(false);
                }}
              >
                Сбросить
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {buyOpen ? (
        <div className="home-top-modal-overlay" role="dialog" aria-modal="true" aria-label="Купить размещение">
          <button type="button" className="home-top-modal-backdrop" aria-label="Закрыть" onClick={() => setBuyOpen(false)} />
          <div className="home-top-modal">
            <div className="home-top-modal__head">
              <h2 className="home-top-modal__title">Купить размещение</h2>
              <button type="button" className="home-top-modal__close" onClick={() => setBuyOpen(false)}>
                ✕
              </button>
            </div>
            <p className="home-top-modal__hint">
              Бесплатный лимит исчерпан ({freePlacementsUsed} из {freePlacementsDefault}). Пакеты за Telegram Stars:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {servicePostingPacks.map((p) => (
                <button
                  key={p.sku}
                  type="button"
                  className="fc-btn"
                  style={{ width: '100%' }}
                  disabled={buyBusy}
                  onClick={() => void buyServicePlacement(p.sku)}
                >
                  {p.slots} размещ. — {p.stars} ⭐
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {buyChannelOpen ? (
        <div
          className="home-top-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Купить буст услуги в канал"
        >
          <button
            type="button"
            className="home-top-modal-backdrop"
            aria-label="Закрыть"
            onClick={() => setBuyChannelOpen(false)}
          />
          <div className="home-top-modal">
            <div className="home-top-modal__head">
              <h2 className="home-top-modal__title">Буст услуги в канал</h2>
              <button type="button" className="home-top-modal__close" onClick={() => setBuyChannelOpen(false)}>
                ✕
              </button>
            </div>
            <p className="home-top-modal__hint">
              VIP даёт {serviceChannelVipQuota} публикаций услуг за период. Дополнительно — пакеты как у
              заказчика (1 / 5 / 10):
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {serviceChannelPacks.map((p) => (
                <button
                  key={p.sku}
                  type="button"
                  className="fc-btn"
                  style={{ width: '100%' }}
                  disabled={buyBusy}
                  onClick={() => void buyServiceChannelPack(p.sku)}
                >
                  {p.slots} в канал — {p.stars} ⭐
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {portfolioView ? (
        <ServicePortfolioPanel
          serviceId={portfolioView.id}
          serviceTitle={portfolioView.title}
          token={token}
          canUpload={portfolioView.canUpload}
          onClose={() => {
            setPortfolioView(null);
            void load();
          }}
        />
      ) : null}
      {detailService ? (
        <ServiceDetailModal
          service={detailService}
          busy={busyId === detailService.id}
          token={token}
          onClose={() => setDetailService(null)}
          onOpenProfile={onOpenProfile}
          onGetService={() => void orderService(detailService.id)}
        />
      ) : null}
    </div>
  );
}

/** Горизонтальная лента популярных услуг на главной */
export function HomePopularServicesStrip({
  token,
  category,
  onCategoryChange,
  onOpenAll,
  onOpenProfile,
  onOrdered
}: {
  token: string;
  category: string;
  onCategoryChange: (v: string) => void;
  onOpenAll: () => void;
  onOpenProfile: (id: number) => void;
  onOrdered: (orderId?: number) => void;
}) {
  const [rows, setRows] = useState<ServiceRow[]>([]);
  const [catsOpen, setCatsOpen] = useState(false);
  const [detailService, setDetailService] = useState<ServiceRow | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ popular: '1', limit: '20' });
        if (category) params.set('category', category);
        const r = await fetch(apiUrl(`/api/executor-services?${params}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        if (!cancelled && r.ok) setRows(d.services || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, category]);

  const orderService = async (id: number) => {
    setBusyId(id);
    try {
      const r = await fetch(apiUrl(`/api/executor-services/${id}/order`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ошибка');
      setDetailService(null);
      const oid = Number(d.order?.id);
      onOrdered(Number.isFinite(oid) ? oid : undefined);
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось заказать');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="home-section-title home-top-section">
        <span>Услуги</span>
        <div className="home-top-section__actions">
          <button type="button" className="home-section-linkish" onClick={() => setCatsOpen(true)}>
            Категории
          </button>
          <button type="button" className="home-section-linkish" onClick={onOpenAll}>
            Все ›
          </button>
        </div>
      </div>
      <div className="home-orders-scroll">
        {rows.length === 0 ? (
          <p className="home-empty-inline">Пока нет услуг в витрине</p>
        ) : (
          rows.map((s) => (
            <button
              key={s.id}
              type="button"
              className="home-order-card home-service-card"
              onClick={() => setDetailService(s)}
            >
              <div className="home-order-card__header">
                <span className="home-service-card__cat">
                  <CategoryEmblem category={s.category} size="sm" className="home-service-card__emblem" />
                  <span className="home-service-card__cat-label">
                    {s.category_label || orderCategoryLabel(s.category)}
                  </span>
                </span>
              </div>
              <div className="home-order-card__title-row home-order-card__title-row--single">
                <span className="home-order-card__title">{s.title}</span>
              </div>
              <div className="home-order-card__price">
                {s.price_usdt_micro != null ? `${formatMicroUsdt(s.price_usdt_micro)} USDT` : '—'}
              </div>
              <div className="home-order-card__meta">{s.executor ? displayNameUser(s.executor) : '—'}</div>
            </button>
          ))
        )}
      </div>

      {detailService ? (
        <ServiceDetailModal
          service={detailService}
          busy={busyId === detailService.id}
          token={token}
          onClose={() => setDetailService(null)}
          onOpenProfile={onOpenProfile}
          onGetService={() => void orderService(detailService.id)}
        />
      ) : null}

      {catsOpen ? (
        <div className="home-top-modal-overlay" role="dialog" aria-modal="true" aria-label="Категории услуг">
          <button
            type="button"
            className="home-top-modal-backdrop"
            aria-label="Закрыть"
            onClick={() => setCatsOpen(false)}
          />
          <div className="home-top-modal">
            <div className="home-top-modal__head">
              <h2 className="home-top-modal__title">Популярные услуги</h2>
              <button type="button" className="home-top-modal__close" onClick={() => setCatsOpen(false)}>
                ✕
              </button>
            </div>
            <p className="home-top-modal__hint">Категория — фильтр ленты на главной.</p>
            <div className="home-top-modal__chips">
              <button
                type="button"
                className={'home-top-chip' + (category === '' ? ' home-top-chip--on' : '')}
                onClick={() => {
                  onCategoryChange('');
                  setCatsOpen(false);
                }}
              >
                Все
              </button>
              {ORDER_CATEGORY_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={'home-top-chip' + (category === c.value ? ' home-top-chip--on' : '')}
                  onClick={() => {
                    onCategoryChange(c.value);
                    setCatsOpen(false);
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const PROFILE_SERVICES_PAGE_SIZE = 5;

/** Услуги исполнителя на профиле: компактный список + модалка детали. */
export function ProfileExecutorServicesBlock({
  token,
  executorUserId,
  canOrder,
  onOrdered,
  embedded
}: {
  token: string;
  executorUserId: number;
  canOrder?: boolean;
  onOrdered?: (orderId?: number) => void;
  /** Без внешней карточки — внутри общего блока с «предложить задание» */
  embedded?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailService, setDetailService] = useState<ServiceRow | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const pageCount = Math.max(1, Math.ceil(total / PROFILE_SERVICES_PAGE_SIZE));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: String(executorUserId),
        limit: String(PROFILE_SERVICES_PAGE_SIZE),
        offset: String(page * PROFILE_SERVICES_PAGE_SIZE)
      });
      const r = await fetch(apiUrl(`/api/executor-services?${params}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ошибка');
      setServices(Array.isArray(d.services) ? d.services : []);
      setTotal(Number(d.total) || 0);
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось загрузить услуги');
      setServices([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, executorUserId, page]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  useEffect(() => {
    setPage(0);
    setOpen(false);
    setServices([]);
    setTotal(0);
  }, [executorUserId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          user_id: String(executorUserId),
          limit: '1',
          offset: '0'
        });
        const r = await fetch(apiUrl(`/api/executor-services?${params}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        if (!cancelled && r.ok) setTotal(Number(d.total) || 0);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, executorUserId]);

  const orderService = async (id: number) => {
    if (!canOrder) {
      showAlert('Заказать услугу может только заказчик');
      return;
    }
    setBusyId(id);
    try {
      const r = await fetch(apiUrl(`/api/executor-services/${id}/order`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ошибка');
      setDetailService(null);
      onOrdered?.(d.order?.id != null ? Number(d.order.id) : undefined);
    } catch (e: any) {
      showAlert(e?.message || 'Не удалось оформить услугу');
    } finally {
      setBusyId(null);
    }
  };

  const body = (
    <>
      <button
        type="button"
        className="fc-btn fc-btn--ghost fc-btn--block fc-btn--sm"
        onClick={() => setOpen((v) => !v)}
        style={{ textAlign: 'left', fontWeight: 650 }}
      >
        {open ? '▲ Скрыть услуги' : '🛠 Услуги исполнителя'}
        {total > 0 ? (
          <span style={{ opacity: 0.75, fontWeight: 500 }}> ({total})</span>
        ) : null}
      </button>

      {open ? (
        <div style={{ marginTop: 8 }}>
          {loading ? (
            <p className="fc-muted" style={{ margin: 0, fontSize: 12 }}>
              Загрузка…
            </p>
          ) : services.length === 0 ? (
            <p className="fc-muted" style={{ margin: 0, fontSize: 12 }}>
              Нет активных услуг на витрине.
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {services.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setDetailService(s)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      background: 'rgba(255,255,255,0.04)',
                      borderRadius: 10,
                      padding: '7px 10px',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'inherit',
                      cursor: 'pointer'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 10
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          lineHeight: 1.25,
                          flex: 1,
                          minWidth: 0
                        }}
                      >
                        {s.title}
                      </span>
                      <span
                        style={{
                          flexShrink: 0,
                          fontSize: 13,
                          fontWeight: 700,
                          color: 'var(--fc-usdt)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {s.price_usdt_micro != null
                          ? `${formatMicroUsdt(s.price_usdt_micro)} USDT`
                          : '—'}
                      </span>
                    </div>
                    <div style={{ marginTop: 2, fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.35 }}>
                      {s.category_label || orderCategoryLabel(s.category)}
                    </div>
                  </button>
                ))}
              </div>

              {total > PROFILE_SERVICES_PAGE_SIZE ? (
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8
                  }}
                >
                  <button
                    type="button"
                    className="fc-btn fc-btn--ghost fc-btn--sm"
                    disabled={page <= 0 || loading}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    ←
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--fc-text-muted)' }}>
                    {page + 1} / {pageCount}
                  </span>
                  <button
                    type="button"
                    className="fc-btn fc-btn--ghost fc-btn--sm"
                    disabled={page + 1 >= pageCount || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    →
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {detailService ? (
        <ServiceDetailModal
          service={detailService}
          busy={busyId === detailService.id}
          token={token}
          hideExecutor
          onClose={() => setDetailService(null)}
          onGetService={() => void orderService(detailService.id)}
        />
      ) : null}
    </>
  );

  if (embedded) return <div style={{ marginTop: 8 }}>{body}</div>;
  return <div className="fc-section-card" style={{ marginTop: 12 }}>{body}</div>;
}
